import React, { useState } from "react";
import { ChatInterface } from "../../../components/chat/ChatInterface";
import type { ChatMessage } from "../../../components/chat/ChatInterface";
import { HomeProductShortcuts } from "../../../components/dashboard/HomeProductShortcuts";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { cn } from "../../../lib/utils";
import { IncomingOrgInvitationsCard } from "./IncomingOrgInvitationsCard";
import type { MainAppPageProps, UserDisplay } from "../types";

type TeammateHomePageProps = Pick<
  MainAppPageProps,
  | "organization"
  | "membershipRole"
  | "routes"
  | "hasCredentials"
  | "hasJobSitePhotos"
  | "csrfToken"
  | "incomingOrgInvitations"
  | "initialMessage"
  | "userDisplay"
> & {
  onUserDisplayChange?: (updates: Partial<UserDisplay>) => void;
};

export const TeammateHomePage: React.FC<TeammateHomePageProps> = ({
  organization,
  membershipRole,
  routes,
  hasCredentials,
  hasJobSitePhotos,
  csrfToken,
  incomingOrgInvitations = [],
  initialMessage,
  userDisplay,
  onUserDisplayChange,
}) => {
  const credentialsPath =
    routes.dashboardCredentials ?? "/dashboard/credentials";
  const jobsPath = routes.dashboardJobs ?? "/dashboard/jobs";
  const chatPath = routes.teammateChat ?? "/teammate/chat";
  const [messages, setMessages] = useState<ChatMessage[]>(() =>
    initialMessage ? [{ role: "assistant", content: initialMessage }] : [],
  );
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isProcessing || !input.trim()) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setIsProcessing(true);

    try {
      const response = await fetch(chatPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ message: userMessage }),
      });
      const data = (await response.json()) as {
        message?: string;
        error?: string;
        display_name?: string;
      };
      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }
      const savedName = data.display_name?.trim();
      if (savedName) {
        onUserDisplayChange?.({ userName: savedName });
      }
      if (data.message) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.message as string },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "error",
          content:
            error instanceof Error
              ? error.message
              : "Could not send that message.",
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative flex w-full min-h-0 flex-1 flex-col lg:min-h-0">
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col items-stretch gap-6 pt-4",
          "lg:h-full lg:flex-row lg:overflow-hidden",
        )}
      >
        <div className="flex w-full shrink-0 flex-col gap-6 lg:w-[380px] lg:overflow-y-auto">
          <div className="shrink-0">
            <h1 className="ds-page-title">Home</h1>
            <p className="ds-page-subtitle mt-2">
              {organization?.name ?? "Your team"}
            </p>
            <HomeProductShortcuts
              jobsHref={jobsPath}
              credentialsHref={credentialsPath}
              showWebsite={false}
              showJobs={Boolean(hasJobSitePhotos)}
              showCredentials={Boolean(hasCredentials)}
            />
          </div>
          <IncomingOrgInvitationsCard
            csrfToken={csrfToken}
            routes={routes}
            invitations={incomingOrgInvitations}
            needsDisplayName={Boolean(userDisplay?.needsDisplayName)}
          />
          {membershipRole === "viewer" ? (
            <Card className="bg-card">
              <CardHeader>
                <CardTitle className="text-base">Viewer</CardTitle>
                <CardDescription>Photo capture is limited.</CardDescription>
              </CardHeader>
            </Card>
          ) : null}
        </div>

        <div
          className={cn(
            "flex min-h-[28rem] min-w-0 flex-1 flex-col overflow-hidden",
            "rounded-2xl border border-border bg-card shadow-card",
            "lg:min-h-0",
          )}
          data-testid="teammate-home-chat"
        >
          <ChatInterface
            messages={messages}
            input={input}
            onInputChange={setInput}
            onSend={handleSubmit}
            isProcessing={isProcessing}
            placeholder="Ask how to set up Toolkit…"
            header={{
              title: "ToolKit Assistant",
              subtitle: "Help with Jobs, Credentials, and Settings",
            }}
            containerClassName="flex min-h-0 flex-1 flex-col"
            messagesClassName="flex-1 min-h-0 space-y-6 overflow-y-auto p-4 sm:p-6"
            userAvatar={userDisplay?.avatarUrl}
            userName={userDisplay?.userName}
          />
        </div>
      </div>
    </div>
  );
};
